import { cn } from "@/utils/cn";
import { CitationSidebar } from "@/features/chat/components/CitationSidebar";
import { MessageList } from "@/features/chat/components/MessageList";
import { PromptInput, type PromptContext } from "@/features/chat/components/PromptInput";
import { ChatSessionProvider, useChatSession } from "@/features/chat/context/ChatSessionProvider";
import { CitationProvider, useCitations } from "@/features/chat/context/CitationProvider";
import { AGENT_CONFIG_PATH } from "@/features/chat/types";
import { PanelRightClose } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { type ChatCell, cellChips, cellContextText, chatCellKey } from "../chatCells";
import { FadeText } from "./Fade";

const STARTERS = [
  "Summarize what these studies find",
  "Which rows are the strongest evidence, and why?",
  "Where do the studies disagree?",
  "What is missing from this dataset?",
];

const sessionKey = (chatKey: string) => `fast-dataset-chat:${chatKey}`;
const WIDTH_KEY = "fast-dataset-chat-width";
/** The agent's edits to the dataset; chats from before the rename ran `paperclip fast-dataset`. */
const DATASET_COMMAND = /\bpaperclip (?:dataset|datasets|fast-dataset)\b/;
const MIN_WIDTH = 300;
const MAX_WIDTH = 1600;
/** Narrow until the user starts chatting, then wider; a width the user dragged to always wins. */
const IDLE_WIDTH = 340;
const ENGAGED_WIDTH = 560;

const storedWidth = () => {
  const n = Number(window.localStorage.getItem(WIDTH_KEY));
  return n >= MIN_WIDTH && n <= MAX_WIDTH ? n : null;
};

/** Drag on the left edge of the panel; `scale` is the page zoom, so screen pixels become CSS pixels. */
const useDragWidth = (scale: number, fallback: number) => {
  const [dragged, setWidth] = useState<number | null>(storedWidth);
  const width = dragged ?? fallback;
  const [dragging, setDragging] = useState(false);
  const start = (e: React.MouseEvent) => {
    e.preventDefault();
    const x0 = e.clientX;
    const w0 = width;
    let last = w0;
    setDragging(true);
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    const move = (ev: MouseEvent) => {
      last = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, w0 - (ev.clientX - x0) / scale));
      setWidth(last);
    };
    const up = () => {
      setDragging(false);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      window.localStorage.setItem(WIDTH_KEY, String(Math.round(last)));
      document.removeEventListener("mousemove", move);
      document.removeEventListener("mouseup", up);
    };
    document.addEventListener("mousemove", move);
    document.addEventListener("mouseup", up);
  };
  return { width, dragging, start };
};

/** The right panel: a Paperclip agent chat that gets the dataset, how it was built and every row.
 * With a share token instead of an id, the agent can answer questions about the dataset but not change it. */
export const FastDatasetChat = ({
  datasetId,
  shareToken,
  question,
  scale = 1,
  onClose,
  onTurnEnd,
  cells = [],
  onCellsChange,
}: {
  datasetId?: string;
  shareToken?: string;
  question: string;
  scale?: number;
  onClose: () => void;
  /** Cells of the table attached to the next message. */
  cells?: ChatCell[];
  onCellsChange?: (cells: ChatCell[]) => void;
  /** When the agent may have changed the dataset: an edit command finished, or a reply ended. */
  onTurnEnd?: () => void;
}) => {
  const chatKey = datasetId ?? `shared:${shareToken}`;
  const [sessionId, setSessionId] = useState<string | undefined>(
    () => window.localStorage.getItem(sessionKey(chatKey)) ?? undefined,
  );
  const [engaged, setEngaged] = useState(() => sessionId != null || cells.length > 0);
  const drag = useDragWidth(scale, engaged ? ENGAGED_WIDTH : IDLE_WIDTH);
  const remember = useCallback(
    (id: string) => {
      window.localStorage.setItem(sessionKey(chatKey), id);
      setSessionId(id);
    },
    [chatKey],
  );
  const restart = () => {
    window.localStorage.removeItem(sessionKey(chatKey));
    setSessionId(undefined);
  };

  return (
    <aside
      className={cn(
        "relative flex h-full shrink-0 flex-col border-l border-zinc-200 bg-white",
        !drag.dragging && "motion-safe:transition-[width] motion-safe:duration-200 motion-safe:ease-out",
      )}
      style={{ width: drag.width }}
      onFocusCapture={() => setEngaged(true)}
      onPointerDownCapture={(e) => {
        if (e.target !== e.currentTarget.firstElementChild) setEngaged(true);
      }}
      aria-label="Chat with this dataset"
    >
      <div
        aria-hidden
        onMouseDown={drag.start}
        className={cn(
          "absolute -left-[3px] bottom-0 top-0 z-10 w-[6px] cursor-col-resize transition-colors hover:bg-zinc-300/50",
          drag.dragging && "bg-zinc-300/60",
        )}
      />
      <div className="flex shrink-0 items-center justify-between gap-2 border-b border-zinc-200 px-4 py-2.5">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-zinc-900">Chat with this dataset</p>
          <FadeText className="text-xs text-zinc-500" title={question}>
            {datasetId ? question : `Shared, view only · ${question}`}
          </FadeText>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {sessionId && (
            <button type="button" onClick={restart} className="font-ui px-2 py-1 text-xs text-zinc-500 hover:text-zinc-900">
              New chat
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            aria-label="Hide chat"
            className="inline-flex h-8 w-8 items-center justify-center rounded-[3px] text-zinc-500 hover:bg-zinc-100 hover:text-zinc-800"
          >
            <PanelRightClose className="h-4 w-4" aria-hidden />
          </button>
        </div>
      </div>
      <ChatSessionProvider
        key={sessionId ?? `new:${chatKey}`}
        sessionId={sessionId}
        agentConfigPath={AGENT_CONFIG_PATH}
        onSessionCreated={remember}
        fastDatasetId={datasetId}
        fastDatasetShare={datasetId ? undefined : shareToken}
      >
        <CitationProvider>
          <ChatBody fresh={!sessionId} onTurnEnd={onTurnEnd} datasetId={datasetId} cells={cells} onCellsChange={onCellsChange} />
        </CitationProvider>
      </ChatSessionProvider>
    </aside>
  );
};

const ChatBody = ({
  fresh,
  onTurnEnd,
  datasetId,
  cells,
  onCellsChange,
}: {
  fresh: boolean;
  onTurnEnd?: () => void;
  datasetId?: string;
  cells: ChatCell[];
  onCellsChange?: (cells: ChatCell[]) => void;
}) => {
  const context = useMemo<PromptContext>(() => {
    const chips = cellChips(cells);
    return {
      chips,
      text: cells.length ? cellContextText(datasetId, cells) : "",
      onRemove: (key) => {
        const gone = new Set(chips.find((chip) => chip.key === key)?.keys ?? []);
        onCellsChange?.(cells.filter((c) => !gone.has(chatCellKey(c.rowKey, c.columnKey))));
      },
      onSent: () => onCellsChange?.([]),
    };
  }, [cells, datasetId, onCellsChange]);
  const { selectedCitation, closeCitation } = useCitations();
  const { sendMessage, isStreaming, traceTurns } = useChatSession();
  const wasStreaming = useRef(isStreaming);
  useEffect(() => {
    if (wasStreaming.current && !isStreaming) onTurnEnd?.();
    wasStreaming.current = isStreaming;
  }, [isStreaming, onTurnEnd]);
  const edits = useMemo(
    () =>
      traceTurns
        .flatMap((turn) => turn.items ?? [])
        .flatMap((item) =>
          item.type === "tool_call" &&
          item.toolCall.status !== "pending" &&
          item.toolCall.status !== "running" &&
          DATASET_COMMAND.test(String(item.toolCall.input?.command ?? ""))
            ? [item.toolCall.id]
            : [],
        )
        .join(","),
    [traceTurns],
  );
  const seenEdits = useRef(edits);
  useEffect(() => {
    if (edits === seenEdits.current) return;
    seenEdits.current = edits;
    if (edits) onTurnEnd?.();
  }, [edits, onTurnEnd]);
  const ask = (text: string) => void sendMessage(text);
  if (selectedCitation) {
    return (
      <div className="min-h-0 flex-1 overflow-hidden">
        <CitationSidebar onClose={closeCitation} />
      </div>
    );
  }
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <MessageList
        compact
        emptyState={
          fresh ? (
            <div className="px-4 py-5 text-sm leading-6 text-zinc-600">
              <p>
                The agent has this dataset: how it was searched, the passages it read, and every column and row with the
                sentence each value came from. It can also read the documents in full and search beyond them.
              </p>
              <div className="mt-4 flex flex-col items-start gap-2">
                {STARTERS.map((starter) => (
                  <button
                    key={starter}
                    type="button"
                    onClick={() => ask(starter)}
                    className="rounded-[3px] border border-zinc-200 px-2.5 py-1 text-left text-[13px] text-zinc-700 hover:border-zinc-400 hover:text-zinc-900"
                  >
                    {starter}
                  </button>
                ))}
              </div>
            </div>
          ) : undefined
        }
      />
      <PromptInput compact context={context} placeholder={cells.length ? "Ask about these cells…" : "Ask about this dataset…"} />
    </div>
  );
};
